import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { SidePanelInterface } from "../types";
import CloseIcon from '@mui/icons-material/Close';
import React from "react";

// Animation keyframes
const slideIn = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const slideOut = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
`;

const blurFadeIn = keyframes`
  from {
    backdrop-filter: blur(0px);
  }
  to {
    backdrop-filter: blur(1.5px);
  }
`;

const blurFadeOut = keyframes`
  from {
    backdrop-filter: blur(1.5px);
  }
  to {
    backdrop-filter: blur(0px);
  }
`;

const fadeInBackground = keyframes`
  from {
    background: transparent;
  }
  to {
    background: rgba(15, 20, 25, 0.25);
  }
`;

const fadeOutBackground = keyframes`
  from {
    background: rgba(15, 20, 25, 0.25);
    }
    to {
      background: transparent;
  }
`;

const Wrapper = styled.div<{ $isVisible: boolean }>`
  width: 100vw;
  min-height: 100vh;
  overflow: auto;
  backdrop-filter: ${props => (props.$isVisible ? 'blur(1px)' : 'blur(0px)')};
  background: ${props => (props.$isVisible ? 'rgba(15, 20, 25, 0.25)' : 'transparent')};
  inset: 0px;
  position: fixed;
  right: 0px;
  top: 0px;
  z-index: 10;
  display: flex;
  flex-direction: row;
  animation: ${props =>
    props.$isVisible ? fadeInBackground : fadeOutBackground} 0.1s ease-out,
    ${props => (props.$isVisible ? blurFadeIn : blurFadeOut)} 0.1s ease-out;
`;

const RightPanel = styled.div<{ $isVisible: boolean }>`
  box-sizing: border-box;
  background-color: white;
  min-height: 100vh;
  position: absolute;
  top: 0px;
  right: 0px;
  width: ${props => (props.$isVisible ? '100%' : '0')}; /* Dynamically set width */
  animation: ${props => (props.$isVisible ? slideIn : slideOut)} 0.3s ease-out;
  animation-fill-mode: forwards;

  // For larger screens, limit the width of the panel
  @media (min-width: 768px) {
    width: ${props => (props.$isVisible ? '50vw' : '0')}; /* Dynamically set width */
    max-width: 600px;
  }

  // Full width for smaller screens
  @media (max-width: 768px) {
    padding-bottom: 100px;
    width: ${props => (props.$isVisible ? '100vw' : '0')}; /* Dynamically set width */
  }
`;

const PanelHeader = styled.div`
  display: flex;
  padding: 20px 0px 10px 40px;
  flex-direction: row;
  align-items: center;
  width: 600px;
  justify-content: flex-end;
  box-sizing: border-box;
  // border-bottom: 1px solid #eaeaea;
  @media (max-width: 768px) {
    width: 100vw;
    padding: 20px 0px 10px 30px;
  }
`;

const HeaderButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-right: auto;
`;

const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  height: 40px;
  cursor: pointer;
  padding:0px 40px;
  @media (max-width: 768px) {
    padding: 0px 30px;
  }
`;

const PanelContent = styled.div`
  overflow: auto;
  box-sizing: border-box;
  padding: 40px;
  width:600px;
  @media (max-width: 768px) {
    width: 100vw;
    padding:40px 30px;
  }
  
`;

// Export the SidePanel component with proper typing
export default function SidePanel({
  isVisible,
  handleHidePanel,
  children,
  headerComponents
}: SidePanelInterface) {
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  // Return null if the panel should not be rendered
  if (!isVisible && !isAnimating) {
    return null;
  }

  const onAnimationEnd = () => {
    setIsAnimating(false);
  };

  const handleClose = () => {
    setIsAnimating(true);
    handleHidePanel();
  };

  return (
    <Wrapper $isVisible={isVisible} onClick={handleClose}>
      <RightPanel
        $isVisible={isVisible}
        onAnimationEnd={onAnimationEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <PanelHeader>
          {
            isVisible && (
              <HeaderButtonsWrapper>
                {
                  headerComponents && headerComponents.map((component, index) => (
                    React.cloneElement(component, { key: index })
                  ))
                }
              </HeaderButtonsWrapper>
            )
          }
          <CloseButton onClick={handleClose}>
            <CloseIcon style={{ fontSize: '19px' }} />
          </CloseButton>
        </PanelHeader>
        <PanelContent style={{ paddingTop: headerComponents ? '40px' : '0' }}>
          {isVisible ? children : null}
        </PanelContent>
      </RightPanel>
    </Wrapper>
  );
}